import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { getRandomId } from '../Utils/baseComponentHelper';
import { FormWrapperPropTypes } from '../Form/withForm';
import useForm from '../Hooks/useForm';
import useActivation from '../Hooks/useActivation';
import useRadioFormChecked from './useRadioFormChecked';

/**
I radio button devono essere utilizzati quando si ha la necessità che l’utente possa vedere tutte le opzioni
disponibili di una determinata scelta.
 */
const Radio = ({
  className,
  disabled: disabledProp,
  readOnly: readOnlyProp,
  name,
  value,
  checked,
  label,
  tooltip,
  onChange,
  ...others
}) => {
  const {
    onFormChange,
    stato,
    notifica,
  } = useForm(name, () => { }, true);

  const uniqueid = useMemo(() => getRandomId(), []);

  const inputChecked = useRadioFormChecked({
    stato, name, value, checked,
  });

  const radioId = `${name}_${value}_${uniqueid}`;

  const { active, disabled, readOnly } = useActivation(disabledProp, readOnlyProp);

  const classes = cns((label ? undefined : className), {
    radio: !label,
    'radio--disabled': disabled,
    'radio--enabled': active,
    'radio--readOnly': readOnly,
  });

  const classesLabel = cns(className, {
    radio: label,
    'radio--labeled': label,
    'label--disabled': disabled,
    'label--readOnly': readOnly,
    'label--checked': inputChecked,
    'label--unchecked': !inputChecked,
  });

  const handleChange = useCallback((e) => {
    if (active) notifica(e, name, e.target.value, onChange, onFormChange, stato);
  }, [active, name, notifica, onChange, onFormChange, stato]);

  return (
    label ? (
      <label className={classesLabel} htmlFor={radioId} title={tooltip}>
        <span className={classes}>
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...others}
            id={radioId}
            disabled={disabled}
            checked={inputChecked}
            onChange={handleChange}
            title={tooltip}
            type="radio"
            value={value}
          />
          <span />
        </span>
        <span className="radio__label">{label}</span>
      </label>
    ) : (
      <span className={classes}>
        <input
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...others}
          disabled={disabled}
          checked={inputChecked}
          onChange={handleChange}
          title={tooltip}
          type="radio"
          value={value}
        />
        <span />
      </span>
    ));
};

Radio.propTypes = {
  ...FormWrapperPropTypes,
  /** Indica se il campo è selezionato */
  checked: PropTypes.bool,
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Indica se il componente è readOnly */
  readOnly: PropTypes.bool,
  /** Nome del campo da bindare se il componente è contenuto all'interno di un componente Form */
  name: PropTypes.string,
  /**
Funzione invocata alla selezione di un elemento.
dove:

- `event`: evento scatenante
   */
  onChange: PropTypes.func,
  /** Indica il valore bindato al campo */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  /** Etichetta da associare al radio button */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Tooltip da visualizzare nell'hover del radio */
  tooltip: PropTypes.string,
};

Radio.defaultProps = {
  checked: false,
  className: '',
  disabled: false,
  readOnly: false,
  name: '',
  onChange: null,
  value: null,
  label: null,
  tooltip: null,
};

export default Radio;
